import {Badge, Button, Icon, mount_carousel} from '@bitstillery/common/components/portal'
import {MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import {$t, api} from '@bitstillery/common/app'
import {AvailabilityType, OfferType, ProductPhotoType} from '@bitstillery/common/api/fact2server'
import type {CreateEntityResponse} from '@bitstillery/common/api/fact2server'
import m from '@bitstillery/mithril'

import {$m} from '@/app'
import type {OfferItemPrice} from '@/lib/price'
import type {CartOfferItem} from '@/models/cart'
import {$s} from '@/app'

interface ProductImageAttrs {
    className?: string
    ribbon?: boolean // Show the offer ribbon
    offer_item: CartOfferItem
}

export class ProductImage extends MithrilComponent<ProductImageAttrs> {
    get_availability_button_type(offer_item: Pick<CartOfferItem, 'expected_entry_date_in_weeks' | 'offer_item_type'>) {
        if (offer_item.offer_item_type === AvailabilityType.Tbo) {
            return {icon: 'tbo', type: 'brand'}
        } else if (offer_item.offer_item_type === AvailabilityType.Purchase && offer_item.expected_entry_date_in_weeks) {
            return {icon: 'eta', type: 'info'}
        }

        return {icon: 'stock', type: 'success'}
    }

    show_updated_ribbon(offer_item: CartOfferItem, offer_item_price: OfferItemPrice) {
        // Don't show the ribbon if there is a discount or the item is unavailable, as these ribbons take precedence.
        if (offer_item_price.has_discount || offer_item.offer_item_type === AvailabilityType.Unavailable) {
            return false
        }
        const seen_on = offer_item.update_seen_on
        const last_week = Date.now() - 7 * 24 * 60 * 60 * 1000
        // Show the ribbon if the update has not been seen and the first time the user saw the update was within the last week
        return seen_on === null || (seen_on !== undefined && seen_on !== offer_item.created_on && Date.parse(seen_on) > last_week)
    }

    view(vnode: m.Vnode<ProductImageAttrs>) {
        const {offer_item} = vnode.attrs
        // Always render a wrapper to avoid DOM gaps; show a placeholder until photos are available
        const photos = $s.product_photos[offer_item.artkey]
        const has_photos_entry = photos !== undefined

        const offer_item_price = $m.offer.offer_item_price(offer_item, $s.cart.errors)
        const image_location = has_photos_entry ? $m.offer.primary_photo(vnode.attrs.offer_item.artkey) : undefined

        const slides = photos
            ? photos
                  .filter((photo) => photo.photo_type === ProductPhotoType.PRODUCT_PHOTO)
                  .map((photo) => ({url: photo.image_location}))
            : []

        return (
            <div
                className={classes('c-product-image', vnode.attrs.className, {
                    'carousel-active': slides.length,
                })}
                onclick={() => {
                    if (!photos || slides.length === 0) {
                        return
                    }

                    mount_carousel({
                        controls: true,
                        current: 0,
                        indicators: true,
                        slides: photos.map((photo) => ({url: photo.image_location})),
                        type: 'fullscreen',
                    })
                }}
            >
                <div className='product-media'>
                    {image_location ? (
                        <img decoding='async' loading='lazy' src={image_location} />
                    ) : (
                        <Icon className='image-placeholder' name='bottlePlaceholder' type='unset' />
                    )}
                    {vnode.children}
                </div>

                {/* Photo count — only when there are real photos to view (illustrations get a clean corner),
                    revealed on hover (see .btn-photos in the scss). A quiet "N photos, click to view" cue next
                    to the favourite, not a control on every card. Clicking it bubbles up to open the carousel. */}
                {slides.length > 0 && (
                    <Button className='btn-photos' icon='image' size='xs' type='surface' variant='toggle'>
                        <Badge className='btn-photos-count' type='brand'>
                            {slides.length}
                        </Badge>
                    </Button>
                )}

                <Button
                    active={offer_item.favourite_artkey}
                    className='btn-favourite'
                    icon={offer_item.favourite_artkey ? 'favourite' : 'favourite-o'}
                    onclick={async (e) => {
                        e.stopPropagation()
                        if (offer_item.favourite_artkey) {
                            const {success} = await api.delete(`portal/offer-items/${offer_item.case_artkey}/favourite`)
                            if (success) {
                                offer_item.favourite_artkey = null
                                $s.offers.favourites -= 1
                            }
                        } else {
                            // The route declares only OkResponse, but a new favourite answers 201 with its artkey.
                            const {result, success} = await api.post<CreateEntityResponse>(
                                `portal/offer-items/${offer_item.case_artkey}/favourite`,
                                {},
                                true,
                            )
                            if (success) {
                                offer_item.favourite_artkey = result.artkey
                                $s.offers.favourites += 1
                            }
                        }
                    }}
                    size='xs'
                    tip={() => {
                        if (vnode.attrs.offer_item.favourite_artkey) return $t('offer.my_favourites_remove')
                        return $t('offer.my_favourites_add')
                    }}
                    type={offer_item.favourite_artkey ? 'brand' : 'surface'}
                    variant='toggle'
                />

                {offer_item_price.has_discount && (
                    <div className={classes('c-product-ribbon', offer_item.offer_type)}>
                        {offer_item.offer_type === OfferType.Spotlight && $t('offer.best_deal')}
                        {offer_item.offer_type === OfferType.Offer && $t('offer.name')}
                    </div>
                )}
                {offer_item.offer_item_type === AvailabilityType.Unavailable && (
                    <div className='c-product-ribbon unavailable'>{$t('offer.unavailable')}</div>
                )}
                {this.show_updated_ribbon(offer_item, offer_item_price) && (
                    <div className='c-product-ribbon updated'>
                        {offer_item.offer_item_type === AvailabilityType.Stock && $t('offer.back_in_stock')}
                        {offer_item.offer_item_type === AvailabilityType.Purchase && $t('offer.arriving_soon')}
                        {offer_item.offer_item_type === AvailabilityType.Tbo && $t('offer.back_in_tbo')}
                    </div>
                )}
            </div>
        )
    }
}
